import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CeederoApiError } from "@/api/ceedero-api";
import { ceederoApi } from "@/api/client";

export interface AccountProfile {
  avatarRevision: string | null;
  displayName: string | null;
  id: string;
  licenseDisplayKey: string;
  /**
   * Null only for an account holding no credentials, which cannot reach this
   * route — a session requires them. Typed as the API types it: declared
   * non-null here once, and every consumer read undefined off it.
   */
  username: string | null;
}

export const accountProfileQueryKey = ["account", "profile"] as const;

/**
 * Who is signed in, if anybody.
 *
 * This is the whole of "am I signed in": the session cookie is `HttpOnly` and
 * unreadable, so the only honest way to answer is to ask. A `401` is not an
 * error to report but the answer "nobody", which is why it is not retried.
 */
export const useAccountProfile = () =>
  useQuery({
    queryKey: accountProfileQueryKey,
    queryFn: () => ceederoApi.request<AccountProfile>({ path: "/v1/me/profile" }),
    retry: (failureCount, error) =>
      !(error instanceof CeederoApiError && error.status === 401) && failureCount < 2,
  });

export const useSignIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { password: string; username: string }) =>
      ceederoApi.request<void>({
        body: credentials,
        method: "POST",
        path: "/v1/web/sessions",
      }),
    // Nothing about the session is in the response - it is an HttpOnly cookie -
    // so the profile is what tells the application it worked.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountProfileQueryKey }),
  });
};

export const useSignOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      ceederoApi.request<void>({ method: "DELETE", path: "/v1/web/sessions/current" }),
    // Everything cached was fetched as somebody; none of it survives them
    // leaving.
    onSuccess: () => queryClient.clear(),
  });
};

/**
 * Changing the account name shown beside the username. Optional, and cleared by
 * sending `null` rather than an empty string — "no display name" is a state the
 * API models, and `""` is a name made of nothing.
 */
export const useUpdateDisplayName = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string | null) =>
      ceederoApi.request<{ displayName: string | null }>({
        body: { displayName },
        method: "PATCH",
        path: "/v1/me/profile",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountProfileQueryKey }),
  });
};

/**
 * Changing the username. The current password travels with it because a session
 * is evidence that somebody signed in once, possibly hours ago on an unattended
 * machine, not that the person typing now knows the password.
 *
 * Nothing is revoked by this: a session names a user, not a name.
 */
export const useChangeUsername = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: { currentPassword: string; username: string }) =>
      ceederoApi.request<{ username: string }>({
        body: change,
        method: "PATCH",
        path: "/v1/me/username",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountProfileQueryKey }),
  });
};

/**
 * Changing the password, which revokes every other session for the account —
 * every other browser, and every desktop installation's stored credential. This
 * browser is spared, because it is the one that asked.
 *
 * Nothing is invalidated on success: the password is in no cache to update, and
 * the profile did not change.
 */
export const useChangePassword = () =>
  useMutation({
    mutationFn: (change: { currentPassword: string; newPassword: string }) =>
      ceederoApi.request<void>({
        body: change,
        method: "PATCH",
        path: "/v1/me/password",
      }),
  });

/**
 * Signing out every browser, this one included — anything else would not be
 * what the control says. Desktop installations keep their own credential and
 * are unaffected; only a password change reaches those.
 */
export const useSignOutEverywhere = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => ceederoApi.request<void>({ method: "DELETE", path: "/v1/me/web-sessions" }),
    onSuccess: () => queryClient.clear(),
  });
};

/**
 * Replacing the account's picture.
 *
 * The file is sent as its own bytes under its own media type, which is what the
 * API reads to decide whether it will accept them at all — there is no
 * multipart form and no base64 in a JSON field, both of which would inflate the
 * payload to say the same thing.
 *
 * The profile is invalidated because it carries the revision the picture is
 * addressed by: without that, the new avatar sits behind a five-minute cache
 * entry for the old one and the upload reads as having silently failed.
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      ceederoApi.request<{ avatarRevision: string }>({
        body: file,
        method: "PUT",
        path: "/v1/me/avatar",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountProfileQueryKey }),
  });
};

export const useRemoveAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => ceederoApi.request<void>({ method: "DELETE", path: "/v1/me/avatar" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountProfileQueryKey }),
  });
};

/**
 * Asking for a recovery code to be mailed to the purchase email.
 *
 * It succeeds for every address, because the API answers the same whether or
 * not that address ever bought Ceedero — so success here means "the form was
 * sent", never "that address is a customer".
 */
export const useRequestRecovery = () =>
  useMutation({
    mutationFn: (email: string) =>
      ceederoApi.request<void>({
        body: { email },
        method: "POST",
        path: "/v1/accounts/recovery",
      }),
  });

/**
 * Setting a new password against the mailed code. No session comes back: the
 * user signs in next, exactly as on any other day, and the username is returned
 * because it is forgotten as often as the password is.
 */
export const useCompleteRecovery = () =>
  useMutation({
    mutationFn: (recovery: { code: string; newPassword: string }) =>
      ceederoApi.request<{ username: string }>({
        body: recovery,
        method: "POST",
        path: "/v1/accounts/recovery/complete",
      }),
  });

/**
 * Deleting the account. The session that asked is gone with it, so everything
 * cached goes too — and the next question about who is signed in answers
 * "nobody", which is what draws the sign-in page.
 */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (currentPassword: string) =>
      ceederoApi.request<void>({
        body: { currentPassword },
        method: "DELETE",
        path: "/v1/me",
      }),
    onSuccess: () => queryClient.clear(),
  });
};
